<template>
  <div class="page-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">上架</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <div class="tabs">
        <button
          class="tab"
          :class="{ active: activeTab === 'todo' }"
          @click="switchTab('todo')"
        >
          待上架
        </button>
        <button
          class="tab"
          :class="{ active: activeTab === 'done' }"
          @click="switchTab('done')"
        >
          已完成
        </button>
      </div>

      <div class="modal-search">
        <input
          v-model="supplierKeyword"
          class="search-input"
          placeholder="请输入供应商名称搜索"
          @input="handleSupplierSearch"
          @focus="supplierDropdown = true"
        />
        <button
          v-if="selectedSupplier || supplierKeyword"
          class="input-clear"
          type="button"
          @click="clearSelectedSupplier"
          aria-label="清除供应商"
        >
          ×
        </button>
        <div v-if="!selectedSupplier && supplierKeyword" class="filter-hint">请选择下拉中的供应商进行筛选</div>
        <div v-if="supplierDropdown" class="dropdown-menu">
          <div v-if="supplierLoading" class="dropdown-loading">加载中...</div>
          <div v-else-if="supplierList.length === 0" class="dropdown-empty">暂无数据</div>
          <div
            v-else
            v-for="s in supplierList"
            :key="s.id"
            class="dropdown-item"
            :class="{ selected: selectedSupplier && selectedSupplier.id === s.id }"
            @click="selectSupplier(s)"
          >
            {{ s.supplierName }}
          </div>
        </div>
      </div>

      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p class="loading-text">加载中...</p>
      </div>

      <div v-else>
        <div v-if="list.length === 0" class="empty-state">
          <img src="@/assets/无数据.svg" alt="暂无数据" class="empty-img" />
          <p class="empty-text">~ 暂无数据 ~</p>
        </div>

        <div v-else class="card-list">
          <div class="data-card" v-for="item in list" :key="item.id || item.wareInId || item.wareInNo">
            <div class="receive-card-header">
              <div class="receive-type" :class="getWareInTypeClass(item.wareInType)">{{ getWareInTypeText(item.wareInType) }}</div>
              <div class="receive-no">入库单号：{{ item.wareInNo || '-' }}</div>
            </div>

            <div class="receive-card-body">
              <div class="receive-grid">
                <div class="receive-field">
                  <div class="receive-label">供应商</div>
                  <div class="receive-value">{{ item.supplierName || '-' }}</div>
                </div>
                <div class="receive-field">
                  <div class="receive-label">物料种类</div>
                  <div class="receive-value">{{ item.wareMaterialNum ?? '-' }}</div>
                </div>
                <div class="receive-field">
                  <div class="receive-label">来货总数</div>
                  <div class="receive-value">{{ formatQty(item.wareTotalNum || item.wareInNum || '-') }}</div>
                </div>
                <div class="receive-field">
                  <div class="receive-label">上架总数</div>
                  <div class="receive-value">{{ formatQty(item.wareInNum || '-') }}</div>
                </div>
              </div>

              <button v-if="activeTab === 'todo'" class="start-putaway-btn" @click="handleStartPutaway(item)">
                开始上架
              </button>
            </div>
          </div>

          <div class="list-footer">
            <div class="page-info">
              已加载 {{ list.length }} / {{ total }}
            </div>
            <button class="load-more" @click="loadMore" :disabled="loadingMore || finished">
              {{ finished ? '没有更多了' : (loadingMore ? '加载中...' : '加载更多') }}
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getWareInPutawayPage } from '@/api/wareInPutaway'
import { getSupplierPageGtZero } from '@/api/wareInReceiveCreate'
import toast from '@/utils/toast'

export default {
  name: 'WareInPutaway',
  setup() {
    const router = useRouter()
    const route = useRoute()

    const activeTab = ref('todo')
    const current = ref(1)
    const size = ref(10)

    const list = ref([])
    const total = ref(0)

    const loading = ref(false)
    const loadingMore = ref(false)

    const statusMsg = ref('')
    const statusType = ref('info')

    const supplierKeyword = ref('')
    const supplierLoading = ref(false)
    const supplierDropdown = ref(false)
    const supplierList = ref([])
    const selectedSupplier = ref(null)
    const supplierTimer = ref(0)

    const wareInStatus = computed(() => (activeTab.value === 'todo' ? 2 : 3))
    const finished = computed(() => list.value.length >= total.value && total.value > 0)

    const handleBack = () => router.back()
    const goHome = () => router.push('/home')

    const getWareInTypeText = (val) => {
      if (val === 0 || val === '0') return '收货入库'
      if (val === 1 || val === '1') return '退货入库'
      if (val === 4 || val === '4') return '计划取消'
      return '收货入库'
    }

    const getWareInTypeClass = (val) => {
      if (val === 0 || val === '0') return 'type-0'
      if (val === 1 || val === '1') return 'type-1'
      if (val === 4 || val === '4') return 'type-4'
      return 'type-0'
    }

    const formatTime = (val) => {
      if (!val) return '-'
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return String(val)
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mi = String(d.getMinutes()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
    }

    const formatNum = (val) => {
      if (val === null || val === undefined || val === '') return '-'
      const n = Number(val)
      if (Number.isNaN(n)) return String(val)
      return n.toFixed(2)
    }

    const formatQty = (val) => {
      if (val === null || val === undefined || val === '') return '-'
      const n = Number(val)
      if (Number.isNaN(n)) return String(val)
      return n.toFixed(3)
    }

    const handleStartPutaway = (item) => {
      const id = item?.id || item?.wareInId
      if (!id) {
        toast.error('缺少入库单ID')
        return
      }
      const basePath = router.currentRoute.value.path.startsWith('/mobile') ? '/mobile/warein-putaway' : '/warein-putaway'
      router.push(`${basePath}/detail/${id}`)
    }

    const handleSupplierSearch = () => {
      supplierDropdown.value = true
      if (supplierTimer.value) window.clearTimeout(supplierTimer.value)
      supplierTimer.value = window.setTimeout(async () => {
        await fetchSuppliers()
      }, 250)
    }

    const fetchSuppliers = async () => {
      if (supplierLoading.value) return
      supplierLoading.value = true
      try {
        const res = await getSupplierPageGtZero({
          supplierName: supplierKeyword.value.trim(),
          current: 1,
          size: 20
        })
        supplierList.value = res?.data?.records || []
      } catch (e) {
        supplierList.value = []
        toast.error(e?.message || '供应商加载失败')
      } finally {
        supplierLoading.value = false
      }
    }

    const selectSupplier = async (s) => {
      selectedSupplier.value = s
      supplierDropdown.value = false
      supplierKeyword.value = s?.supplierName || ''
      await fetchPage({ reset: true })
    }

    const clearSelectedSupplier = async () => {
      selectedSupplier.value = null
      supplierKeyword.value = ''
      supplierList.value = []
      supplierDropdown.value = false
      await fetchPage({ reset: true })
    }

    const fetchPage = async ({ reset } = { reset: false }) => {
      if (reset) {
        current.value = 1
        list.value = []
        total.value = 0
      }

      loading.value = reset
      loadingMore.value = !reset
      statusMsg.value = ''

      try {
        const res = await getWareInPutawayPage({
          current: current.value,
          size: size.value,
          wareInStatus: wareInStatus.value,
          supplierId: selectedSupplier.value?.id
        })

        const records = res?.data?.records || []
        const nextList = reset ? records : list.value.concat(records)

        list.value = nextList
        total.value = res?.data?.total || nextList.length

        if (nextList.length === 0) {
          statusMsg.value = ''
        }
      } catch (e) {
        statusMsg.value = e?.message || '加载失败'
        statusType.value = 'error'
      } finally {
        loading.value = false
        loadingMore.value = false
      }
    }

    const switchTab = async (tab) => {
      if (activeTab.value === tab) return
      activeTab.value = tab
      await fetchPage({ reset: true })
    }

    const loadMore = async () => {
      if (finished.value || loading.value || loadingMore.value) return
      current.value += 1
      await fetchPage({ reset: false })
    }

    onMounted(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      fetchPage({ reset: true })
    })

    watch(
      () => route.query.refresh,
      async (v) => {
        if (!v) return
        await fetchPage({ reset: true })
        const nextQuery = { ...route.query }
        delete nextQuery.refresh
        router.replace({ path: route.path, query: nextQuery })
      }
    )

    return {
      activeTab,
      list,
      total,
      loading,
      loadingMore,
      finished,
      statusMsg,
      statusType,
      handleBack,
      goHome,
      switchTab,
      loadMore,
      formatTime,
      formatNum,
      formatQty,
      supplierKeyword,
      supplierLoading,
      supplierDropdown,
      supplierList,
      selectedSupplier,
      handleSupplierSearch,
      selectSupplier,
      clearSelectedSupplier,
      handleStartPutaway,
      getWareInTypeText,
      getWareInTypeClass
    }
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 18px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(64px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  border: none;
  background: transparent;
  padding: 6px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-back {
  width: 22px;
  height: 22px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: var(--header-text);
  margin: 0;
  flex: 1;
  text-align: center;
}

.header-right {
  width: 40px;
  height: 40px;
}

.content {
  padding: 10px 12px;
  margin-top: calc(64px + env(safe-area-inset-top));
}

.tabs {
  display: flex;
  background: var(--card-background);
  border-radius: 12px;
  padding: 6px;
  gap: 6px;
  box-shadow: 0 2px 10px var(--shadow-light);
}

.tab {
  flex: 1;
  border: none;
  background: transparent;
  padding: 10px 0;
  border-radius: 10px;
  font-size: 15px;
  color: var(--text-secondary);
}

.tab.active {
  background: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
  font-weight: 600;
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #ef4444;
  color: #ffffff;
  font-size: 12px;
  line-height: 18px;
  margin-left: 6px;
}

.modal-search {
  position: relative;
  margin-top: 10px;
}

.search-input {
  width: 100%;
  height: 38px;
  padding: 0 34px 0 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  background: var(--card-background);
  box-sizing: border-box;
}

.input-clear {
  position: absolute;
  right: 6px;
  top: 6px;
  width: 26px;
  height: 26px;
  border-radius: 13px;
  border: none;
  background: var(--background-color);
  color: var(--text-primary);
  font-size: 18px;
}

.filter-hint {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.dropdown-menu {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  border-radius: 10px;
  box-shadow: 0 10px 26px var(--shadow-light);
  border: 1px solid var(--border-color);
  background: var(--card-background);
  overflow: hidden;
  max-height: 260px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 50;
}

.dropdown-loading,
.dropdown-empty {
  padding: 12px;
  font-size: 13px;
  color: var(--text-secondary);
}

.dropdown-item {
  padding: 12px 12px;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
}

.dropdown-item + .dropdown-item {
  border-top: 1px solid var(--border-color);
}

.dropdown-item:hover {
  background: var(--background-color);
}

.dropdown-item.selected {
  background: rgba(35, 177, 192, 0.12);
  font-weight: 600;
}

.dropdown-item:active {
  background: var(--background-color);
}

.status-banner {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 13px;
}

.status-banner.info {
  background: rgba(29, 78, 216, 0.1);
  color: #1d4ed8;
}

.status-banner.error {
  background: rgba(185, 28, 28, 0.1);
  color: #b91c1c;
}

.loading-state {
  padding: 36px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid rgba(35, 177, 192, 0.2);
  border-top-color: var(--primary-color);
  animation: spin 0.9s linear infinite;
}

.loading-text {
  margin-top: 10px;
  color: var(--text-secondary);
  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  margin-top: 28px;
  background: var(--card-background);
  border-radius: 16px;
  padding: 40px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 4px 14px var(--shadow-light);
}

.empty-img {
  width: 140px;
  height: 140px;
  opacity: 0.95;
}

.empty-text {
  margin-top: 12px;
  color: var(--text-light);
  font-size: 14px;
}

.card-list {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.data-card {
  background: var(--card-background);
  border-radius: 16px;
  padding: 14px 14px 12px;
  box-shadow: 0 4px 14px var(--shadow-light);
  border: 1px solid var(--border-color);
}

.receive-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.receive-type {
  color: #ffffff;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.receive-type.type-0 {
  background: #23b1c0;
}

.receive-type.type-1 {
  background: #f59e0b;
}

.receive-type.type-4 {
  background: #6b7280;
}

.receive-no {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.receive-card-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.receive-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
}

.receive-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.receive-label {
  color: var(--text-secondary);
  font-size: 12px;
}

.receive-value {
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
}

.start-putaway-btn {
  align-self: flex-end;
  border: none;
  background: var(--primary-color);
  color: #ffffff;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 700;
  min-width: 110px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  max-width: calc(100% - 88px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.badge.done {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.label {
  color: var(--text-secondary);
  font-size: 13px;
  flex: 0 0 auto;
}

.value {
  color: var(--text-primary);
  font-size: 13px;
  text-align: right;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row.highlight .value {
  color: var(--primary-color);
  font-weight: 700;
}

.list-footer {
  margin-top: 4px;
  background: var(--card-background);
  border-radius: 16px;
  padding: 12px 12px;
  box-shadow: 0 4px 14px var(--shadow-light);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.page-info {
  color: var(--text-secondary);
  font-size: 12px;
}

.load-more {
  border: none;
  background: var(--primary-color);
  color: #ffffff;
  border-radius: 999px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  min-width: 110px;
}

.load-more:disabled {
  background: #cbd5e1;
}

.home-fab {
  position: fixed;
  right: 14px;
  top: calc(64px + env(safe-area-inset-top) + 90px);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: var(--card-background);
  box-shadow: 0 10px 24px var(--shadow-light);
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-icon {
  width: 22px;
  height: 22px;
}

@media (max-width: 360px) {
  .content {
    padding: 10px 10px;
  }

  .title {
    font-size: 17px;
  }
}
</style>
